import { describirFrecuencia, FRECUENCIA_DIARIA, type Frecuencia } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { conAviso, useTRPC } from "../api.ts";
import { SelectorFrecuencia } from "./Frecuencia.tsx";
import { Aviso, Boton, Tarjeta } from "./ui.tsx";

/**
 * Quién llena un formato de registro y cada cuánto: le aparece en su pantalla "Diligencias" los
 * días que le toca. La frecuencia es de cada asignación (2026-10-20): una persona puede tenerlo
 * los lunes y otra todos los días.
 */
export function ResponsablesDeFormato({ codigo }: { codigo: string }) {
  const trpc = useTRPC();
  const q = useQuery(trpc.hojas.responsables.queryOptions({ codigo }));
  const [elegido, setElegido] = useState("");
  const [frecuencia, setFrecuencia] = useState<Frecuencia>(FRECUENCIA_DIARIA);
  /** La persona cuya frecuencia se está cambiando, y el valor en edición. */
  const [cambiando, setCambiando] = useState<{ id: string; frecuencia: Frecuencia } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const asignar = useMutation(
    trpc.hojas.asignar.mutationOptions(
      conAviso(setError, () => {
        setElegido("");
        setFrecuencia(FRECUENCIA_DIARIA);
        setCambiando(null);
      }),
    ),
  );
  if (!q.data) return null;
  const r = q.data;
  if (!r.puedeAsignar && r.asignados.length === 0) return null;
  return (
    <Tarjeta
      titulo="Lo llenan todos los días"
      ayuda="A cada persona le aparece en Diligencias los días que le toca, con la hoja lista para llenar."
      className="mb-4"
    >
      {error && <Aviso className="mb-3">{error}</Aviso>}
      <div className="flex flex-wrap items-center gap-2">
        {r.asignados.length === 0 && (
          <span className="text-[13px] text-texto-3">Nadie todavía.</span>
        )}
        {r.asignados.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-2 rounded-full border border-borde bg-fondo py-1 pr-1.5 pl-3 text-[13px]"
          >
            <b className="font-semibold">{p.nombre}</b>
            {p.puesto && <span className="text-texto-3">{p.puesto}</span>}
            {r.puedeAsignar ? (
              <button
                type="button"
                title="Cambiar cada cuánto le toca"
                disabled={asignar.isPending}
                onClick={() =>
                  setCambiando(
                    cambiando?.id === p.id ? null : { id: p.id, frecuencia: p.frecuencia },
                  )
                }
                className="rounded-full bg-white px-2 py-0.5 text-[11.5px] text-marino hover:bg-marino-claro"
              >
                {describirFrecuencia(p.frecuencia)}
              </button>
            ) : (
              <span className="text-[11.5px] text-texto-3">
                {describirFrecuencia(p.frecuencia)}
              </span>
            )}
            {r.puedeAsignar && (
              <button
                type="button"
                aria-label={`Quitar a ${p.nombre}`}
                title="Quitar"
                disabled={asignar.isPending}
                onClick={() => asignar.mutate({ codigo, usuarioId: p.id, asignado: false })}
                className="grid size-6 place-items-center rounded-full text-texto-3 hover:bg-rojo-fondo hover:text-rojo"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {cambiando && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-borde/70 bg-fondo px-3 py-2 text-[13px]">
          <span className="text-texto-2">
            A {r.asignados.find((p) => p.id === cambiando.id)?.nombre ?? "esta persona"} le toca
          </span>
          <SelectorFrecuencia
            valor={cambiando.frecuencia}
            onChange={(f) => setCambiando({ id: cambiando.id, frecuencia: f })}
            disabled={asignar.isPending}
          />
          <Boton
            disabled={asignar.isPending}
            onClick={() =>
              asignar.mutate({
                codigo,
                usuarioId: cambiando.id,
                asignado: true,
                frecuencia: cambiando.frecuencia,
              })
            }
          >
            Guardar
          </Boton>
          <Boton variante="secundario" onClick={() => setCambiando(null)}>
            Cancelar
          </Boton>
        </div>
      )}
      {r.puedeAsignar && r.candidatos.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={elegido}
            onChange={(e) => setElegido(e.target.value)}
            aria-label="Persona que lo llena"
            className="min-h-[40px] min-w-[240px] rounded-md border border-borde bg-white px-3 text-[13px]"
          >
            <option value="">Agregar a alguien…</option>
            {r.candidatos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
                {c.puesto ? ` · ${c.puesto}` : ""}
              </option>
            ))}
          </select>
          <SelectorFrecuencia valor={frecuencia} onChange={setFrecuencia} />
          <Boton
            disabled={!elegido || asignar.isPending}
            onClick={() =>
              asignar.mutate({ codigo, usuarioId: elegido, asignado: true, frecuencia })
            }
          >
            Agregar
          </Boton>
        </div>
      )}
    </Tarjeta>
  );
}
